import { omit, toPairs } from "ramda";

import {
    type DocumentSetStructure,
    type DocumentSubscriptionData,
    type LitAPIParams,
    type LitStructureAPIParams,
    type ParamVal,
    type SortableByRank,
    type StructureResponse,
    type SubscribableAssetData,
} from "@hsl/core/documents/types";

export async function fetchDocStructure(
    confId: number,
    params: LitStructureAPIParams,
) {
    // const { activeShareClassId } = params;
    const createParams = ({
        base_url: _,
        ...params
    }: LitAPIParams | LitStructureAPIParams): string => {
        const reducer = (
            urlparams: URLSearchParams,
            [key, value]: [string, ParamVal],
        ): URLSearchParams => {
            if (Array.isArray(value)) {
                return value.reduce(
                    (urlparams: URLSearchParams, v: string | number) =>
                        reducer(urlparams, [key, v]),
                    urlparams,
                );
            }
            urlparams.append(key, `${value}`);
            return urlparams;
        };
        return Object.entries({ ...params, subscribable_assets: "true" })
            .reduce(reducer, new URLSearchParams())
            .toString();
    };

    const byRank =
        (order: "asc" | "desc") => (a: SortableByRank, b: SortableByRank) =>
            order === "asc" ? a.rank - b.rank : b.rank - a.rank;
    const byRankAsc = byRank("asc");

    const setsRes = await fetch(
        `${
            params.base_url ?? ""
        }/srp/api/literature-structure/${confId}/?${createParams(params)}`,
    );
    const sets = (await setsRes.json()) as StructureResponse;
    const data = sets.data
        .filter((d) => d.document_count > 0)
        .toSorted(byRankAsc);
    const subscribable_asset_data = sets.subscribable_asset_data;
    return data.map((d) => ({
        ...d,
        subscription_data: getSubscriptionData(
            d,
            subscribable_asset_data,
        ) as DocumentSubscriptionData,
    }));
}

function getSubscriptionData(
    d: DocumentSetStructure,
    s: SubscribableAssetData,
) {
    const isShareClassLevel = Boolean(d.settings?.shareClassSelector);
    // const log = <T>(x: T): T => {
    //     console.log(x);
    //     return x;
    // };
    const subDataForDoc = Object.entries(s)
        .flatMap(([fundId, litSet]) =>
            Object.entries(litSet)
                .filter(([set_id]) => set_id === `${d.set_id}`)
                .map(([_, lSet]) => ({
                    ...lSet,
                    fundId,
                    funds: [fundId],
                    shareClasses: isShareClassLevel
                        ? Object.keys(lSet.by_share_class)
                        : ([] as string[]),
                    subsMap: isShareClassLevel
                        ? toPairs(lSet.by_share_class).map(([scId, subsId]) => [
                              fundId,
                              scId,
                              subsId,
                          ])
                        : [[fundId, null, lSet.sub_asset_id]],
                })),
        )
        .reduce((sData, item) => {
            return {
                ...sData,
                funds: sData.funds.concat(item.funds),
                shareClasses: sData.shareClasses.concat(item.shareClasses),
                subsMap: sData.subsMap.concat(item.subsMap),
            };
        });
    return omit(
        ["fundId", "by_share_class", "sub_asset_id", "display_name"],
        subDataForDoc,
    );
}
